import { Injectable } from '@angular/core';

// external libs
import { debounceTime, takeUntil } from 'rxjs/operators';

// services
import { H21MapClusterService } from './h21-map-marker-cluster-service';

// enums
import { ChangesMap } from '../enums/changes-map.enum';
import { EventType } from '../enums/event-type.enum';

// models
import { CircleEvent } from '../models/circle-event.model';
import { AreaEvent } from '../models/area-event.model';
import { MarkerClusterFilter } from '../models/marker-cluster-filter.model';
import { Bounds } from '../models/bounds-map.model';
import { MarkerClusterFind } from '../models/marker-cluster-find.model';

@Injectable()
export class MarkerClusterEventService {

    public cmp: H21MapClusterService;

    public addEventListenerZoom(): void {
        this.cmp.manager.getMap().events.createEventZoomChange()
            .pipe(
                debounceTime(20),
                takeUntil(this.cmp.destroy$))
            .subscribe({
                next: (event: any) => {
                    if (this.cmp.currentZoom === event.zoom) { return; }
                    if (this.cmp.filter || this.cmp.changeMap === ChangesMap.other) {
                        this.cmp.manager.getMap().markerCluster.removeFeature(this.cmp.layer);
                        this.cmp.filter && this.cmp.getGeoJsonFilter(this.cmp.buildFilter(this.cmp.filter, event.zoom));
                        this.cmp.currentZoom = event.zoom;
                        return;
                    }

                    if (this.cmp.changeMap === ChangesMap.draw) {
                        if (this.cmp.area) {
                            this.cmp.area.zoom = event.zoom;
                            this.cmp.currentZoom = event.zoom;
                            this.cmp.pending$.next(true);
                            this.cmp.manager.getMap().markerCluster.removeFeature(this.cmp.layer);
                            this.cmp.getGeoJson(this.cmp.buildFilterPolygon(this.cmp.area));
                        }

                        if (this.cmp.circle) {
                            this.cmp.circle.zoom = event.zoom;
                            this.cmp.currentZoom = event.zoom;
                            this.cmp.pending$.next(true);
                            this.cmp.manager.getMap().markerCluster.removeFeature(this.cmp.layer);
                            this.cmp.getGeoJson(this.cmp.buildFilterCircle(this.cmp.circle));
                        }
                    }
                },
            });
    }

    public addEventListenerBounds(): void {
        this.cmp.manager.getMap().events.createEventBoundsChange()
            .pipe(
                debounceTime(500),
                takeUntil(this.cmp.destroy$))
            .subscribe({
                next: (event: Bounds) => {
                    if (this.cmp.changeMap === ChangesMap.bbox) {
                        this.cmp.bounds = event;
                        this.cmp.pending$.next(true);
                        this.cmp.manager.getMap().markerCluster.removeFeature(this.cmp.layer);
                        this.cmp.getGeoJson(this.cmp.buildFilterBoundBox(event));
                        this.cmp.currentZoom = event.zoom;
                    }
                },
            });
    }

    public addEventListenerSetMode(): void {
        this.cmp.manager.getMap().markerCluster.changeTrackMap$
            .pipe(
                debounceTime(10),
                takeUntil(this.cmp.destroy$))
            .subscribe({
                next: (mode: ChangesMap) => {
                    this.cmp.changeMap = mode;
                },
            });
    }

    public addEventListenerArea(): void {
        this.cmp.manager.getMap().drawing.createEvent(EventType.drawAreaCreate)
            .pipe(takeUntil(this.cmp.destroy$))
            .subscribe({
                next: (event: AreaEvent) => {
                    this.cmp.isPending && this.cmp.pending$.next(true);
                    this.cmp.area = event;
                    this.cmp.manager.getMap().markerCluster.removeFeature(this.cmp.layer);
                    this.cmp.getGeoJson(this.cmp.buildFilterPolygon(event));
                    this.cmp.filter = null;
                },
            });


        this.cmp.manager.getMap().drawing.createEvent(EventType.drawAreaRemove)
            .pipe(takeUntil(this.cmp.destroy$))
            .subscribe({
                next: () => {
                    this.cmp.isPending && this.cmp.pending$.next(true);
                    this.cmp.area = null;
                    this.cmp.manager.getMap().markerCluster.removeFeature(this.cmp.layer);
                },
            });
    }

    public addEventListenerCircle(): void {
        this.cmp.manager.getMap().markerCluster.modeCircle
            .pipe(takeUntil(this.cmp.destroy$))
            .subscribe({
                next: (event: CircleEvent) => {
                    this.cmp.isPending && this.cmp.pending$.next(true);
                    this.cmp.circle = event;
                    this.cmp.manager.getMap().markerCluster.removeFeature(this.cmp.layer);
                    this.cmp.getGeoJson(this.cmp.buildFilterCircle(event));
                    this.cmp.filter = null;
                },
            });

        this.cmp.manager.getMap().drawing.createEvent(EventType.drawCircleCreate)
            .pipe(takeUntil(this.cmp.destroy$))
            .subscribe({
                next: (event: CircleEvent) => {
                    this.cmp.isPending && this.cmp.pending$.next(true);
                    this.cmp.circle = event;
                    this.cmp.manager.getMap().markerCluster.removeFeature(this.cmp.layer);
                    this.cmp.getGeoJson(this.cmp.buildFilterCircle(event));
                    this.cmp.filter = null;
                },
            });

        this.cmp.manager.getMap().drawing.createEvent(EventType.drawCircleRemove)
            .pipe(takeUntil(this.cmp.destroy$))
            .subscribe({
                next: () => {
                    this.cmp.manager.getMap().markerCluster.removeFeature(this.cmp.layer);
                    this.cmp.circle = null;
                    this.cmp.isPending && this.cmp.pending$.next(true);
                },
            });

        this.cmp.manager.getMap().drawing.createEvent(EventType.drawCircleCentreComplete)
            .pipe(takeUntil(this.cmp.destroy$))
            .subscribe({
                next: (event: CircleEvent) => {
                    this.cmp.circle = event;
                    this.cmp.manager.getMap().markerCluster.removeFeature(this.cmp.layer);
                    this.cmp.getGeoJson(this.cmp.buildFilterCircle(event));
                },
            });

        this.cmp.manager.getMap().drawing.createEvent(EventType.drawCircleRadiusComplete)
            .pipe(takeUntil(this.cmp.destroy$))
            .subscribe({
                next: (event: CircleEvent) => {
                    this.cmp.circle = event;
                    this.cmp.manager.getMap().markerCluster.removeFeature(this.cmp.layer);
                    this.cmp.getGeoJson(this.cmp.buildFilterCircle(event));
                },
            });


        this.cmp.manager.getMap().drawing.createEvent(EventType.drawCircleCentreChange)
            .pipe(takeUntil(this.cmp.destroy$))
            .subscribe({
                next: () => {
                    this.cmp.manager.getMap().markerCluster.removeFeature(this.cmp.layer);
                },
            });

        this.cmp.manager.getMap().drawing.createEvent(EventType.drawCircleRadiusChange)
            .pipe(takeUntil(this.cmp.destroy$))
            .subscribe({
                next: () => {
                    this.cmp.manager.getMap().markerCluster.removeFeature(this.cmp.layer);
                },
            });

    }

    public addEventListenerFilter(): void {
        this.cmp.manager.getMap().markerCluster.changeFilter
            .pipe(takeUntil(this.cmp.destroy$))
            .subscribe({
                next: (event: MarkerClusterFilter) => {
                    if (this.cmp.filter || this.cmp.changeMap === ChangesMap.other) {
                        event ? this.cmp.filter = event : event = this.cmp.filter;
                        this.cmp.manager.getMap().markerCluster.removeFeature(this.cmp.layer);
                        this.cmp.getGeoJsonFilter(this.cmp.buildFilter(event));
                    }

                    if (this.cmp.changeMap === ChangesMap.draw) {
                        this.cmp.manager.getMap().markerCluster.removeFeature(this.cmp.layer);
                        if (this.cmp.area) {
                            this.cmp.area.zoom = this.cmp.currentZoom;
                            this.cmp.getGeoJson(this.cmp.buildFilterPolygon(this.cmp.area));
                        }

                        if (this.cmp.circle) {
                            this.cmp.circle.zoom = this.cmp.currentZoom;
                            this.cmp.getGeoJson(this.cmp.buildFilterCircle(this.cmp.circle));
                        }
                    }

                    if (this.cmp.changeMap === ChangesMap.bbox) {
                        this.cmp.pending$.next(true);
                        if (this.cmp.bounds) {
                            this.cmp.manager.getMap().markerCluster.removeFeature(this.cmp.layer);
                            this.cmp.getGeoJson(this.cmp.buildFilterBoundBox(this.cmp.bounds));
                        }
                    }
                },
            });
    }

    public addEventListenerIsMarkerCluster(): void {
        this.cmp.manager.getMap().markerCluster.isMarkerCluster
            .pipe(
                takeUntil(this.cmp.destroy$))
            .subscribe({
                next: (position: any) => {
                    this.cmp.requestId && this.cmp.getIsMarkerCluster(this.cmp.buildIsMarkerCluster(new MarkerClusterFind({
                        showOnMapRequestId: this.cmp.requestId,
                        location: position,
                    })));

                },
            });

        this.cmp.manager.getMap().markerCluster.resizeBounds$
            .pipe(
                takeUntil(this.cmp.destroy$))
            .subscribe({
                next: (bounds?: Bounds) => {
                    if (this.cmp.changeMap === ChangesMap.bbox) {
                        this.cmp.pending$.next(true);
                        this.cmp.bounds = bounds;
                        if (!this.cmp.bounds) {
                            this.cmp.bounds = this.cmp.manager.getMap().conversions.translateBounds();
                        }

                        this.cmp.manager.getMap().markerCluster.removeFeature(this.cmp.layer);
                        this.cmp.getGeoJson(this.cmp.buildFilterBoundBox(this.cmp.bounds));
                    }

                },
            });
    }

    public addEventListenerTilesLoad(): void {
        this.cmp.manager.getMap().events.createEventTilesload()
            .pipe(
                debounceTime(15),
                takeUntil(this.cmp.destroy$))
            .subscribe({
                next: (bounds?: Bounds) => {
                    if (this.cmp.changeMap === ChangesMap.bbox && bounds) {
                        this.cmp.pending$.next(true);
                        this.cmp.bounds = bounds;
                        this.cmp.manager.getMap().markerCluster.removeFeature(this.cmp.layer);
                        this.cmp.getGeoJson(this.cmp.buildFilterBoundBox(this.cmp.bounds));
                    }

                },
            });

        this.cmp.manager.getMap().markerCluster.clearMarkerCluster$
            .pipe(
                debounceTime(15),
                takeUntil(this.cmp.destroy$))
            .subscribe({
                next: () => {
                    this.cmp.pending$.next(true);

                },
            });

    }

}
